import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Table, Button, Card, Modal, Badge, border, ButtonGroup } from "react-bootstrap";
import DashboardCalendarWidget from "./components/calendar/DashboardCalendarWidget";
import PageTitle from './components/PageTitle';
import moment from "moment";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import CaseStatisticsPage from "./CaseStatisticsPage";
import { API_ENDPOINTS } from './config/api';
import { getRotationTypesForSpecialty, DEFAULT_ROTATION_COLORS } from './utils/rotationTypesConfig';

// Helper function to capitalize names
const capitalizeName = (name) => {
  return name.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

// Helper function to render conference type with label and color
const renderConferenceType = (type) => {
  const typeLabels = {
    'mm': 'M&M',
    'ground_rounds': 'GR',
    'both': 'M&M and GR',
    'educational': 'Education',
    'journal_club': 'Journal'
  };
  
  const typeColors = {
    'mm': 'primary',
    'ground_rounds': 'success',
    'both': 'warning',
    'educational': 'info',
    'journal_club': 'secondary'
  };
  
  return {
    label: typeLabels[type] || type,
    color: typeColors[type] || 'secondary'
  };
};

const ResidentDashboard = () => {
  const [residentName, setResidentName] = useState("Resident");
  const [assignedEvals, setAssignedEvals] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(moment().startOf("week"));
  const [weekDates, setWeekDates] = useState([]);
  const [logData, setLogData] = useState({});
  const [rotationInfo, setRotationInfo] = useState(null);
  const [caseStats, setCaseStats] = useState(null);
  const [persistedCaseTrends, setPersistedCaseTrends] = useState({});
  const [showDetailedStats, setShowDetailedStats] = useState(false);
  const [upcomingVacations, setUpcomingVacations] = useState(null);
  const [monthlyWorkData, setMonthlyWorkData] = useState([]);
  const [monthlyStatsLoading, setMonthlyStatsLoading] = useState(false);
  const [showDetailedWorkStats, setShowDetailedWorkStats] = useState(false);
  const [allConferences, setAllConferences] = useState(null);
  const [conferenceView, setConferenceView] = useState('thisWeek'); // 'thisWeek' or 'upcoming'
  const [sidebarSections, setSidebarSections] = useState([]);
  const [sidebarSectionsLoading, setSidebarSectionsLoading] = useState(false);

  // Helper: get current academic year start (numeric, e.g. 2024 for 2024-2025)
  const getCurrentAcademicYearStart = () => {
    const now = new Date();
    return now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  };
  const [showAllEvaluations, setShowAllEvaluations] = useState(false);
  const [programDetails, setProgramDetails] = useState(null);
  const [rotationColors, setRotationColors] = useState(DEFAULT_ROTATION_COLORS);
  const navigate = useNavigate();
  const location = useLocation();

  const formatRotationRange = (rotationInfoData, which) => {
    const period = which === "upcoming" ? rotationInfoData?.upcomingPeriod : rotationInfoData?.currentPeriod;
    const scheduleType = rotationInfoData?.scheduleType || "monthly";

    if (period?.start && period?.end) {
      return `${moment(period.start).format("MMM D")} - ${moment(period.end).format("MMM D, YYYY")}`;
    }

    // Fallbacks (should be rare now that backend returns periods)
    const now = new Date();
    if (scheduleType === "weekly" || scheduleType === "blocks") {
      const base = which === "upcoming" ? new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7) : now;
      const day = base.getDay();
      const daysToMonday = day === 0 ? 6 : day - 1;
      const monday = new Date(base);
      monday.setDate(base.getDate() - daysToMonday);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return `${moment(monday).format("MMM D")} - ${moment(sunday).format("MMM D, YYYY")}`;
    }

    if (which === "upcoming") {
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const nextMonthEnd = new Date(now.getFullYear(), now.getMonth() + 2, 0);
      return `${moment(nextMonth).format("MMM D")} - ${moment(nextMonthEnd).format("MMM D, YYYY")}`;
    }

    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return `${moment(startDate).format("MMM D")} - ${moment(endDate).format("MMM D, YYYY")}`;
  };

  const getEvaluationIcon = (templateType) => {
    switch (templateType?.toLowerCase()) {
      case 'performance':
        return <i className="bi bi-bar-chart-fill text-primary" />;
      case 'feedback':
        return <i className="bi bi-chat-dots-fill text-info" />;
      case 'survey':
      case 'questionnaire':
        return <i className="bi bi-patch-question-fill text-warning" />;
      default:
        return <i className="bi bi-clipboard-check-fill text-primary" />;
    }
  };

  const getEvaluationIconBg = (templateType) => {
    switch (templateType?.toLowerCase()) {
      case 'performance':
        return 'var(--blue-light)';
      case 'feedback':
        return 'var(--indigo-light)';
      case 'survey':
      case 'questionnaire':
        return 'var(--yellow-light)';
      default:
        return 'var(--blue-light)';
    }
  };

  const getFileIcon = (fileName, mimeType) => {
    if (!fileName && !mimeType) return "bi-file-earmark";
    const ext = fileName ? fileName.split('.').pop().toLowerCase() : '';
    if (mimeType) {
      if (mimeType.includes('pdf')) return "bi-file-earmark-pdf-fill text-danger";
      if (mimeType.includes('word') || mimeType.includes('document')) return "bi-file-earmark-word-fill text-primary";
      if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return "bi-file-earmark-excel-fill text-success";
      if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return "bi-file-earmark-ppt-fill text-warning";
      if (mimeType.includes('image')) return "bi-file-earmark-image-fill text-info";
      if (mimeType.includes('zip') || mimeType.includes('compressed')) return "bi-file-earmark-zip-fill text-secondary";
    }
    if (ext === 'pdf') return "bi-file-earmark-pdf-fill text-danger";
    if (['doc', 'docx'].includes(ext)) return "bi-file-earmark-word-fill text-primary";
    if (['xls', 'xlsx'].includes(ext)) return "bi-file-earmark-excel-fill text-success";
    if (['ppt', 'pptx'].includes(ext)) return "bi-file-earmark-ppt-fill text-warning";
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return "bi-file-earmark-image-fill text-info";
    if (['zip', 'rar'].includes(ext)) return "bi-file-earmark-zip-fill text-secondary";
    return "bi-file-earmark";
  };

  const downloadFile = async (resource) => {
    try {
      const token = localStorage.getItem("token");
      const url = resource.webPath.startsWith('http')
        ? resource.webPath
        : resource.webPath.startsWith('/uploads/')
          ? `${API_ENDPOINTS.PROFILE_PICS}${resource.webPath}`
          : `${API_ENDPOINTS.PROFILE_PICS}/uploads/${resource.webPath}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = resource.originalName || resource.fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      } else {
        alert('Failed to download file. Please try again.');
      }
    } catch (error) {
      console.error("Failed to download file", error);
      alert('Failed to download file. Please try again.');
    }
  };

  // Only set resident name and assigned evals in the initial effect
  useEffect(() => {
    const firstName = localStorage.getItem("firstName");
    const lastName = localStorage.getItem("lastName");
    if (firstName && lastName) {
      setResidentName(firstName + " " + lastName);
    }

    const token = localStorage.getItem("token");
    fetch(`${API_ENDPOINTS.RESIDENT}/assigned`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then(data => setAssignedEvals(Array.isArray(data) ? data : []));

    // Fetch program details to get specialty for rotation colors
    fetch(`${API_ENDPOINTS.PROGRAMS}/my-program-details`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch program details');
        }
        return res.json();
      })
      .then(data => {
        setProgramDetails(data);
        const specialty = data.specialty || data?.program?.specialty;
        if (specialty) {
          const { colors } = getRotationTypesForSpecialty(specialty);
          setRotationColors(colors);
        }
      })
      .catch(err => {
        console.error("Error fetching program details:", err);
      });

    // Fetch rotation information
    fetch(`${API_ENDPOINTS.RESIDENT}/rotation-info`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch rotation info');
        }
        return res.json();
      })
      .then(data => {
        setRotationInfo(data);
      })
      .catch(err => {
        console.error("Error fetching rotation info:", err);
        setRotationInfo(null);
      });

    // Fetch case statistics
    fetch(`${API_ENDPOINTS.RESIDENT}/case-statistics`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch case statistics');
        }
        return res.json();
      })
      .then(data => setCaseStats(data))
      .catch(err => {
        console.error("Error fetching case statistics:", err);
        setCaseStats(null);
      });

    // Fetch upcoming vacations
    fetch(`${API_ENDPOINTS.RESIDENT}/upcoming-vacations`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch upcoming vacations');
        }
        return res.json();
      })
      .then(data => setUpcomingVacations(data))
      .catch(err => {
        console.error("Error fetching upcoming vacations:", err);
        setUpcomingVacations(null);
      });

    // Fetch conferences to show this week's schedule in the dashboard
    const academicYear = getCurrentAcademicYearStart();
    fetch(`${API_ENDPOINTS.CONFERENCES}?academicYear=${academicYear}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch conferences");
        }
        return res.json();
      })
      .then((allConfs) => {
        console.log("Fetched conferences raw:", allConfs);
        // Handle different response structures
        let conferencesArray = [];
        if (Array.isArray(allConfs)) {
          conferencesArray = allConfs;
        } else if (allConfs && Array.isArray(allConfs.data)) {
          conferencesArray = allConfs.data;
        } else if (allConfs && typeof allConfs === 'object') {
          // If it's an object, try to extract arrays from it
          conferencesArray = Object.values(allConfs).flat().filter(Array.isArray).flat();
        }
        
        console.log("Processed conferences array:", conferencesArray);
        const today = moment().startOf("day");
        const startOfCurrentWeek = moment().startOf("week");

        // Build a flat list of conferences - include current week (even past) and future
        // Conferences have a sessions array, each session has a date
        const allUpcoming = conferencesArray
          .flatMap((conf) => {
            // Check if conference has sessions array
            if (conf.sessions && Array.isArray(conf.sessions)) {
              // Conference has multiple sessions - flatten them
              return conf.sessions
                .map((session) => {
                  const sessionDate = session.date;
                  if (!sessionDate) return null;
                  
                  const dateMoment = moment(sessionDate);
                  if (!dateMoment.isValid()) {
                    return null;
                  }
                  // Include conferences from current week onwards (including past dates in current week)
                  if (dateMoment.isBefore(startOfCurrentWeek, "day")) {
                    return null;
                  }

                  return {
                    id: session.id || conf.id,
                    title: conf.title || session.title || "Untitled Conference",
                    type: conf.type || session.type || "",
                    date: sessionDate,
                    startTime: conf.startTime || session.startTime || "",
                    location: session.location || conf.location || "",
                  };
                })
                .filter(Boolean);
            } else if (conf.session) {
              // Single session nested in conference (backward compatibility)
              const session = conf.session;
              const sessionDate = session.date;
              if (!sessionDate) return [];
              
              const dateMoment = moment(sessionDate);
              if (!dateMoment.isValid()) {
                return [];
              }
              // Include conferences from current week onwards (including past dates in current week)
              if (dateMoment.isBefore(startOfCurrentWeek, "day")) {
                return [];
              }

              return [{
                id: session.id || conf.id,
                title: conf.title || session.title || "Untitled Conference",
                type: conf.type || session.type || "",
                date: sessionDate,
                startTime: conf.startTime || session.startTime || "",
                location: session.location || conf.location || "",
              }];
            } else {
              // No sessions found
              return [];
            }
          })
          .sort((a, b) => {
            const aDateTime = moment(`${a.date} ${a.startTime || "00:00"}`, "YYYY-MM-DD HH:mm");
            const bDateTime = moment(`${b.date} ${b.startTime || "00:00"}`, "YYYY-MM-DD HH:mm");
            if (aDateTime.isBefore(bDateTime)) return -1;
            if (aDateTime.isAfter(bDateTime)) return 1;
            return 0;
          });

        console.log("All upcoming conferences:", allUpcoming);
        setAllConferences(allUpcoming);
      })
      .catch((err) => {
        console.error("Error fetching upcoming conferences:", err);
        setAllConferences([]); // Empty array means "no conferences found" vs null which means "still loading"
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchSidebarSections = async () => {
      setSidebarSectionsLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_ENDPOINTS.RESOURCES}/sidebar-sections`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch resource sections");
        const data = await res.json();
        if (!cancelled) {
          setSidebarSections(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (!cancelled) setSidebarSections([]);
      } finally {
        if (!cancelled) setSidebarSectionsLoading(false);
      }
    };

    fetchSidebarSections();
    return () => {
      cancelled = true;
    };
  }, []);

  // Preserve last non-neutral trend until replaced by a new change
  useEffect(() => {
    const nextTrends = caseStats?.trends;
    if (!nextTrends || typeof nextTrends !== "object") return;

    setPersistedCaseTrends((prev) => {
      const updated = { ...prev };
      Object.entries(nextTrends).forEach(([key, trend]) => {
        if (trend === "up" || trend === "down") {
          updated[key] = trend;
        }
      });
      return updated;
    });
  }, [caseStats]);

  // Helper to get week dates from a start moment
  function getWeekDates(start) {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(start.clone().add(i, "days"));
    }
    return dates;
  }

  // Function to fetch monthly work log data
  const fetchMonthlyWorkData = async () => {
    setMonthlyStatsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_ENDPOINTS.RESIDENT}/work-logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const allLogs = await response.json();
        const logsArray = Array.isArray(allLogs) ? allLogs : [];
        
        // Get the last 30 days
        const endDate = moment();
        const startDate = moment().subtract(30, 'days');
        
        // Create data for each day in the last 30 days
        const monthlyData = [];
        const currentDate = startDate.clone();
        
        while (currentDate.isSameOrBefore(endDate)) {
          const dateStr = currentDate.format('YYYY-MM-DD');
          const dayLog = logsArray.find(log => log.date === dateStr) || {};
          
          // Calculate total hours for the day
          let totalMinutes = 0;
          
          // AM hours
          if (dayLog.amStart && dayLog.amEnd && dayLog.amStart !== "00:00" && dayLog.amEnd !== "00:00") {
            const amStart = moment(dayLog.amStart, "HH:mm");
            const amEnd = moment(dayLog.amEnd, "HH:mm");
            if (amEnd.isAfter(amStart)) {
              totalMinutes += amEnd.diff(amStart, "minutes");
            }
          }
          
          // PM hours
          if (dayLog.pmStart && dayLog.pmEnd && dayLog.pmStart !== "00:00" && dayLog.pmEnd !== "00:00") {
            const pmStart = moment(dayLog.pmStart, "HH:mm");
            const pmEnd = moment(dayLog.pmEnd, "HH:mm");
            if (pmEnd.isAfter(pmStart)) {
              totalMinutes += pmEnd.diff(pmStart, "minutes");
            }
          }
          
          const totalHours = totalMinutes / 60;
          
          monthlyData.push({
            date: dateStr,
            day: currentDate.format('MMM D'),
            hours: Math.round(totalHours * 100) / 100, // Round to 2 decimal places
            hasData: totalMinutes > 0,
            isWeekend: currentDate.day() === 0 || currentDate.day() === 6
          });
          
          currentDate.add(1, 'day');
        }
        
        setMonthlyWorkData(monthlyData);
      }
    } catch (error) {
      console.error("Error fetching monthly work data:", error);
    } finally {
      setMonthlyStatsLoading(false);
    }
  };

  // Effect: update weekDates and fetch logs when currentWeekStart or location changes
  useEffect(() => {
    setWeekDates(getWeekDates(currentWeekStart));
    const token = localStorage.getItem("token");
    fetch(`${API_ENDPOINTS.RESIDENT}/work-logs`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(allLogs => {
        const logsArray = Array.isArray(allLogs) ? allLogs : [];
        // Map logs by date for quick lookup
        const logMap = {};
        logsArray.forEach(log => {
          logMap[log.date] = log;
        });
        setLogData(logMap);
      });
  }, [currentWeekStart, location]);

  // Effect: detect week change at midnight and update currentWeekStart
  useEffect(() => {
    const checkWeekChange = () => {
      const nowWeekStart = moment().startOf("week");
      if (!nowWeekStart.isSame(currentWeekStart, "day")) {
        setCurrentWeekStart(nowWeekStart);
      }
    };
    // Check at midnight
    const now = moment();
    const msUntilMidnight = moment().endOf("day").add(1, "ms").diff(now);
    const timeout = setTimeout(() => {
      checkWeekChange();
      // After first check, set interval to check every 24h
      setInterval(checkWeekChange, 24 * 60 * 60 * 1000);
    }, msUntilMidnight);
    return () => clearTimeout(timeout);
  }, [currentWeekStart]);

  const resourceLinkSections = sidebarSections.filter((section) => section?.itemType === "link");
  const resourceDocumentSections = sidebarSections.filter((section) => section?.itemType === "document");

  return (
    <div>
      <PageTitle title="Resident Dashboard" description="View your rotation schedule, work hours, evaluations, and case statistics" />
      <Navbar pageTitle="Resident Dashboard" />
      <Container className="py-4">
        <Row>
          <Col lg={9} md={7} sm={12}>
          {/* Weekly Hours Log - Simple Design */}
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body className="p-md-4">
              {/* Header */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <h6 className="mb-0 fw-semibold">Weekly Hours</h6>
                    <p className="text-muted mb-0 small">Your logged hours for this week</p>
                  </div>
                  <div className="text-center d-none d-md-block">
                    {weekDates.length > 0 && (
                      <>
                        <div className=" small">
                          {weekDates[0].format("MMMM YYYY")}
                        </div>
                        <div className="text-muted small">
                          {moment().format("dddd, MMMM D")}
                        </div>
                      </>
                    )}
                  </div>
                  <Button 
                variant="light-solid" 
                size="sm"
                onClick={() => {
                  if (!showDetailedWorkStats) {
                    fetchMonthlyWorkData();
                  }
                  setShowDetailedWorkStats(!showDetailedWorkStats);
                }}
              >
                {showDetailedWorkStats ? (
                  <>
                    <span className="d-none d-md-block">Show Less</span><i className="bi bi-bar-chart-fill d-block d-md-none"></i>

                  </>
                ) : (
                  <>
                    <span className="d-none d-md-block">View Stats</span><i className="bi bi-bar-chart d-block d-md-none"></i>
                  </>
                )}
              </Button>
                </div>
              </div>

              {/* Calculate week totals */}
              {(() => {
                let totalMinutes = 0;
                let daysCompleted = 0;
                const dayData = weekDates.map((day) => {
                  const d = day.format("YYYY-MM-DD");
                  const entry = logData[d] || {};
                  const hasData = !!(
                    (entry.amStart && entry.amEnd && entry.amStart !== "00:00" && entry.amEnd !== "00:00") ||
                    (entry.pmStart && entry.pmEnd && entry.pmStart !== "00:00" && entry.pmEnd !== "00:00")
                  );
                  
                  let dayTotalMinutes = 0;
                  if (entry.amStart && entry.amEnd && entry.amStart !== "00:00" && entry.amEnd !== "00:00") {
                    const amStart = moment(entry.amStart, "HH:mm");
                    const amEnd = moment(entry.amEnd, "HH:mm");
                    if (amEnd.isAfter(amStart)) {
                      dayTotalMinutes += amEnd.diff(amStart, "minutes");
                    }
                  }
                  if (entry.pmStart && entry.pmEnd && entry.pmStart !== "00:00" && entry.pmEnd !== "00:00") {
                    const pmStart = moment(entry.pmStart, "HH:mm");
                    const pmEnd = moment(entry.pmEnd, "HH:mm");
                    if (pmEnd.isAfter(pmStart)) {
                      dayTotalMinutes += pmEnd.diff(pmStart, "minutes");
                    }
                  }
                  
                  if (hasData) {
                    totalMinutes += dayTotalMinutes;
                    daysCompleted++;
                  }
                  
                  return {
                    day,
                    date: day.format("D"),
                    letter: day.format("ddd")[0],
                    hasData,
                    minutes: dayTotalMinutes
                  };
                });
                
                const totalHours = (totalMinutes / 60).toFixed(2);
                const totalHoursNum = parseFloat(totalHours);
                const avgHours = daysCompleted > 0 ? (totalMinutes / 60 / daysCompleted).toFixed(2) : 0;
                const goalHours = 80; // 80 hours per week
                // Determine compliance status: gray if no records, green if < 80, red if >= 80
                let complianceStatus = 'gray'; // no records
                let isCompliant = false;
                if (daysCompleted > 0) {
                  if (totalHoursNum < 80) {
                    complianceStatus = 'green'; // compliant and less than 80
                    isCompliant = true;
                  } else {
                    complianceStatus = 'red'; // over 80
                    isCompliant = false;
                  }
                }
                
                return (
                  <>
                    {/* Daily Progress Indicators */}
                    <div className=" px-md-5 gap-0 d-flex justify-content-between align-items-center mb-4 flex-wrap">
                      {dayData.map((item, i) => {
                        const hours = (item.minutes / 60).toFixed(2);
                        const displayHours = item.hasData ? hours : '0.00';
                        return (
                          <div
                            key={i}
                            className="d-flex flex-column align-items-center"
                            style={{ 
                              cursor: 'pointer',
                              padding: '1rem',
                              borderRadius: '8px',
                              transition: 'all 0.2s ease',
                              border: '2px solid transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--gray-200)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.borderColor = 'transparent';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                            onClick={() => navigate("/resident/work-hours")}
                          >
                            <div
                              className="d-flex align-items-center justify-content-center rounded-circle"
                              style={{
                                width: '42px',
                                height: '42px',
                                backgroundColor: item.hasData ? 'var(--green)' : 'var(--gray-100)',
                                border: `2px solid ${item.hasData ? 'var(--green)' : 'var(--border-bg)'}`,
                                color: item.hasData ? 'var(--white)' : 'var(--gray-600)',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                marginBottom: '8px'
                              }}
                            >
                              {item.letter}
                            </div>
                            <span
                              style={{
                                fontSize: '14px',
                                color: item.hasData ? 'var(--gray-800)' : 'var(--gray-600)',
                                fontWeight: item.hasData ? '600' : '400',
                                marginBottom: '4px'
                              }}
                            >
                              {item.date}
                            </span>
                            <span
                              style={{
                                fontSize: '12px',
                                color: item.hasData ? 'var(--gray-600)' : 'var(--gray-500)',
                              }}
                            >
                              {displayHours}h
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Summary Statistics */}
                    <div className="d-flex flex-wrap justify-content-between align-items-start border-top gap-4 pt-4 px-md-5">
                      <div className="flex-fill">
                        <div className="text-muted small mb-1">Total Hours</div>
                        <div className="fw-bold" style={{ fontSize: '24px' }}>{totalHours}</div>
                      </div>
                      <div className="flex-fill">
                        <div className="text-muted small mb-1">Average/Day</div>
                        <div className="fw-bold" style={{ fontSize: '24px' }}>{avgHours}</div>
                      </div>
                      <div className="flex-fill">
                        <div className="text-muted small mb-1">Days Worked</div>
                        <div className="fw-bold" style={{ fontSize: '24px' }}>{daysCompleted}/7</div>
                      </div>
                      <div className="flex-fill d-flex flex-column align-items-start">
                       <div className="text-muted small mb-2" style={{ lineHeight: '1.2' }}>
                          Work Hour Compliance Status
                        </div> 
                        <Badge 
                          bg={isCompliant ? 'success' : complianceStatus === 'gray' ? 'secondary' : 'danger'}
                          className="d-flex align-items-center gap-2 mb-2"
                          style={{ fontSize: '14px', padding: '6px 12px' }}
                        >
                          {complianceStatus === 'gray' ? (
                            <>
                              <i className="bi bi-clock"></i>
                              No work hours recorded yet
                            </>
                          ) : (
                            <>
                              <i className={`bi ${isCompliant ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                              {isCompliant ? 'Compliant' : 'Non-compliant'}
                            </>
                          )}
                        </Badge>

                      </div>
                    </div>
                  </>
                );
              })()}
            

            {/* Monthly Work Log Statistics - Collapsible */}
            {showDetailedWorkStats && (
              <>
                {monthlyStatsLoading ? (
                  <div className="text-center py-4 mt-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading monthly statistics...</p>
                  </div>
                ) : (
                  <>
                  <Row className=" mt-4" style={{ borderTop: '1px dashed var(--border-bg)' }}>
                    <Col lg={8} md={12} sm={12}>
 {/* Bar Chart */}
                    <div className="pt-4 mt-4 ">
                      <div className="mb-3">
                        <h6 className="text-muted mb-0 fw-semibold">
                          Daily Hours Worked (Last 30 Days)
                        </h6>
                      </div>
                      <div className="rounded p-3">
                      {monthlyWorkData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart 
                            data={monthlyWorkData} 
                            margin={{ top: 0, right: 0, left: 0, bottom: 0}}
                          >
                            <CartesianGrid strokeDasharray="2 6" stroke="var(--border-bg)" vertical={ false} />
                            <XAxis 
                              dataKey="day" 
                              angle={-45} 
                              textAnchor="end" 
                              height={35}
                              tickLine={false}
                              tick={{ fontSize: 11, fill: 'var(--gray-600)' }}
                              axisLine={{ stroke: 'var(--border-bg)' , strokeWidth: 1 }}
                              interval="preserveStartEnd"
                              minTickGap={20}
                            />
                            <YAxis 
                              tick={{ fontSize: 12, fill: 'var(--gray-600)' }}
                              tickLine={false}
                              axisLine={{ stroke: 'var(--border-bg)' , strokeWidth: 1 }}
                              label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                            />
                            <Tooltip 
                              contentStyle={{
                                backgroundColor: 'var(--card-bg)',
                                border: '1px solid var(--border-bg)',
                                color: 'var(--gray-800)',
                                borderRadius: '8px',
                                boxShadow: 'var(--box-shadow-sm)'
                              }}
                              cursor={{ fill: 'var(--gray-100)' }}
                              formatter={(value, name) => [value + ' hours', 'Hours Worked']}
                              labelFormatter={(label) => {
                                const dayData = monthlyWorkData.find(d => d.day === label);
                                return dayData ? `${label} (${dayData.date})` : label;
                              }}
                            />
                            <Bar 
                              dataKey="hours" 
                              fill="var(--blue)"
                              radius={[4, 4, 0, 0]}
                              barSize={20}
                              cursor={{ fill: 'var(--gray-100)' }}
                            >
                              {monthlyWorkData.map((entry, index) => (
                                <Cell 
                                  key={`cell-${index}`} 
                                  fill={entry.isWeekend ? 'var(--red)' : (entry.hours > 8 ? 'var(--yellow)' : 'var(--green)')}
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-4 text-muted">
                          <i className="bi bi-bar-chart" style={{ fontSize: 48, opacity: 0.3 }}></i>
                          <p className="mt-2">No work log data available</p>
                        </div>
                      )}
                      </div>

                      {/* Legend */}
                      <div className="d-flex justify-content-center gap-4 flex-wrap">
                        <div className="d-flex align-items-center">
                          <div className="rounded me-2" style={{ width: 12, height: 12, backgroundColor: 'var(--bs-success)'}}></div>
                          <small className="text-muted">Normal Hours (≤8)</small>
                        </div>
                        <div className="d-flex align-items-center">
                          <div className="rounded me-2" style={{ width: 12, height: 12, backgroundColor: 'var(--bs-warning)'}}></div>
                          <small className="text-muted">Long Hours (&gt;8)</small>
                        </div>
                        <div className="d-flex align-items-center">
                          <div className="rounded me-2" style={{ width: 12, height: 12, backgroundColor: 'var(--bs-danger)'}}></div>
                          <small className="text-muted">Weekend</small>
                        </div>
                      </div>
                    </div>
                    </Col>
                    <Col lg={4} md={12} sm={12}>
                    <div className="pt-4 mt-4">
                      <div className="d-flex justify-content-between align-items-start gap-4 flex-wrap">
                        <div className="flex-fill">
                          <div className="text-muted small mb-1">Days Worked</div>
                          <div className="fw-bold" style={{ fontSize: '24px' }}>
                            {monthlyWorkData.filter(day => day.hasData).length}
                          </div>
                        </div>
                        <div className="flex-fill">
                          <div className="text-muted small mb-1">Total Hours</div>
                          <div className="fw-bold" style={{ fontSize: '24px' }}>
                            {Math.round(monthlyWorkData.reduce((total, day) => total + day.hours, 0) * 100) / 100}
                          </div>
                        </div>
                        <div className="flex-fill">
                          <div className="text-muted small mb-1">Avg Hours/Day</div>
                          <div className="fw-bold" style={{ fontSize: '24px' }}>
                            {monthlyWorkData.length > 0 ? 
                              Math.round((monthlyWorkData.reduce((total, day) => total + day.hours, 0) / monthlyWorkData.length) * 100) / 100 : 0
                            }
                          </div>
                        </div>
                        <div className="flex-fill">
                          <div className="text-muted small mb-1">Days &gt; 8 Hours</div>
                          <div className="fw-bold" style={{ fontSize: '24px' }}>
                            {monthlyWorkData.filter(day => day.hours > 8).length}
                          </div>
                        </div>
                        <div className="flex-fill">
                          <div className="text-muted small mb-1">Weekend Days</div>
                          <div className="fw-bold" style={{ fontSize: '24px' }}>
                            {monthlyWorkData.filter(day => day.isWeekend && day.hasData).length}
                          </div>
                        </div>
                        <div className="flex-fill">
                          <div className="text-muted small mb-1">Attendance</div>
                          <div className="fw-bold" style={{ fontSize: '24px' }}>
                            {monthlyWorkData.length > 0 ? 
                              Math.round((monthlyWorkData.filter(day => day.hasData).length / monthlyWorkData.length) * 100) : 0
                            }%
                          </div>
                        </div>
                      </div>
                    </div>
                    </Col>
                  </Row>
                    {/* Summary Statistics - Simple Design */}
                    

                   
                  </>
                )}
              </>
            )}
            </Card.Body>
          </Card>

          {/* Rotation Information Card */}
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body className="p-md-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex flex-column align-items-start justify-content-start">
                <h6 className="mb-0 fw-semibold">
                  Rotation & Call Schedule
                </h6>
                <p className="mb-0 text-muted small">Upcoming rotation, call and leave schedule</p>
                </div>
                <Button 
                  variant="light-solid" 
                  size="sm" 
                  onClick={() => navigate("/resident-rotation-scheduler?tab=rotation")}
                >
                  <i className="bi bi-arrow-right"></i>
                </Button>
              </div>
              
              {rotationInfo ? (
                <Row>
                  <Col lg={4} md={6} sm={12}>
                    <div className="d-flex flex-column mb-3 h-100">
                      <small className="text-muted fw-normal mb-2">Rotation Schedule</small>
                      <div className="border-alt  bg-white flex-column rounded-3 p-3 d-flex align-items-start w-100 mb-2">
                        <div className="d-flex align-items-center justify-content-between w-100 mb-1" >
                        <span 
                          className="text-white rounded-3 px-2 py-1 me-3"
                          style={{
                            backgroundColor: rotationColors[rotationInfo.currentRotation] || rotationColors[rotationInfo.currentRotation?.toLowerCase()] || '#3B82F6'
                          }}
                        >
                          <i className="bi bi-calendar-event"/>
                        </span>
                        <Badge bg="primary">
                          current
                        </Badge>
                        </div>

                        <div className="d-flex flex-column">
                          <small className="mb-0 fw-semibold">{rotationInfo.currentRotation}</small>
                          <small className="text-muted">
                            {formatRotationRange(rotationInfo, "current")}
                          </small>
                        </div>
                      </div>

                      <div className="bg-light rounded-3 p-3 d-flex flex-column align-items-start h-100">
                        <div className="d-flex align-items-center justify-content-between w-100 mb-1">
                        <small className="text-muted fw-semibold">Upcoming</small>
                        <i className="bi bi-clock text-muted opacity-50"></i>
                        </div>
                        <div className="d-flex align-items-center justify-content-start w-100">
                        <span 
                          className="text-white rounded-3 px-2 py-1 me-3"
                          style={{
                            backgroundColor: rotationColors[rotationInfo.upcomingRotation] || rotationColors[rotationInfo.upcomingRotation?.toLowerCase()] || '#F59E0B'
                          }}
                        >
                          <i className="bi bi-calendar-event" />
                        </span>


                        <div className="d-flex flex-column">
                          <small className="mb-0 fw-semibold">{rotationInfo.upcomingRotation}</small>
                          <small className="text-muted">
                            {formatRotationRange(rotationInfo, "upcoming")}
                          </small>
                        </div>
                      </div>
                      </div>
                    </div>
                  </Col>
                  <Col lg={4} md={6} sm={12} className="mt-2 mt-lg-0 mt-md-0mt-sm-0">
                    <div className="d-flex flex-column h-100">
                      <small className="text-muted fw-normal mb-2">
                        Weekend Call Schedule
                      </small>
                      {(() => {
                        const currentResidentName = capitalizeName(residentName);
                        
                        // Check current week
                        const currentWeekendCall = rotationInfo?.currentWeekendCall;
                        const currentSaturdayData = currentWeekendCall?.saturday?.type === 'Saturday' ? currentWeekendCall.saturday : null;
                        const currentSundayData = currentWeekendCall?.sunday || (currentWeekendCall?.saturday?.type === 'Sunday' ? currentWeekendCall.saturday : null);
                        const isOnCallCurrentSaturday = currentSaturdayData?.residents?.some(r => capitalizeName(r.name) === currentResidentName);
                        const isOnCallCurrentSunday = currentSundayData?.residents?.some(r => capitalizeName(r.name) === currentResidentName);
                        const isOnCallCurrentWeek = isOnCallCurrentSaturday || isOnCallCurrentSunday;
                        
                        // Check next week
                        const upcomingWeekendCall = rotationInfo?.upcomingWeekendCall;
                        const upcomingSaturdayData = upcomingWeekendCall?.saturday?.type === 'Saturday' ? upcomingWeekendCall.saturday : null;
                        const upcomingSundayData = upcomingWeekendCall?.sunday || (upcomingWeekendCall?.saturday?.type === 'Sunday' ? upcomingWeekendCall.saturday : null);
                        const isOnCallUpcomingSaturday = upcomingSaturdayData?.residents?.some(r => capitalizeName(r.name) === currentResidentName);
                        const isOnCallUpcomingSunday = upcomingSundayData?.residents?.some(r => capitalizeName(r.name) === currentResidentName);
                        const isOnCallNextWeek = isOnCallUpcomingSaturday || isOnCallUpcomingSunday;
                        
                        // If not on call for both weeks, show combined message
                        if (!isOnCallCurrentWeek && !isOnCallNextWeek) {
                          return (
                            <div className="h-100 text-muted p-3 border-alt bg-white rounded-3 d-flex flex-column align-items-center justify-content-center h-100">
                              <img src="/images/undraw_relaxing-at-home_qta9.svg" alt="No weekend call" className="img-fluid mb-2" style={{ maxHeight: '100px' }} />
                              <p className="mb-0 small">You are not on call for the next weeks</p>
                            </div>
                          );
                        }
                        
                        return (
                          <>
                            {/* Current Week */}
                            {currentWeekendCall ? (
                              isOnCallCurrentWeek ? (
                                <div className="mb-2 px-3 py-2 border-alt bg-white rounded-3 h-100">
                                  <small className="mb-2 fw-semibold">This Week's Call</small>
                                  <div>
                                    {isOnCallCurrentSaturday && currentSaturdayData && (
                                      <div className="mb-1 border-dashed-bottom">
                                        <small className="mb-1 fw-semibold">Saturday: {moment(currentSaturdayData.date).format('MMM D, YYYY')}</small>
                                        <div className="d-flex flex-wrap">
                                          {currentSaturdayData.residents.map((resident, idx) => (
                                            <span key={idx} className={`small ${capitalizeName(resident.name) === currentResidentName ? 'text-success' : 'text-muted'} me-1 mb-1`}>
                                              {capitalizeName(resident.name)}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {isOnCallCurrentSunday && currentSundayData && (
                                      <div className="mb-1">
                                        <small className="mb-1 fw-semibold">Sunday: {moment(currentSundayData.date).format('MMM D, YYYY')}</small>
                                        <div className="d-flex flex-wrap">
                                          {currentSundayData.residents.map((resident, idx) => (
                                            <span key={idx} className={`small ${capitalizeName(resident.name) === currentResidentName ? 'text-success' : 'text-muted'} me-1 mb-1`}>
                                              {capitalizeName(resident.name)}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted p-2 border-alt bg-white rounded-3 mb-2">
                                  <img src="/images/undraw_relaxing-at-home_qta9.svg" alt="No weekend call" className="img-fluid mb-2" style={{ maxHeight: '100px' }} />
                                  <p className="mb-0 small">You are not on call this week</p>
                                </div>
                              )
                            ) : (
                              <div className="text-muted mb-2">
                                <em>No weekend call scheduled</em>
                              </div>
                            )}
                            
                            {/* Next Week */}
                            {upcomingWeekendCall ? (
                              isOnCallNextWeek ? (
                                <div className="h-100 px-3 py-2 bg-light rounded">
                                  <small className="mb-2 fw-semibold text-muted">Next Week's Call</small>
                                  <div>
                                    {isOnCallUpcomingSaturday && upcomingSaturdayData && (
                                      <div className="mb-1 pb-2 border-dashed-bottom">
                                        <small className="mb-1 fw-semibold">Saturday: {moment(upcomingSaturdayData.date).format('MMM D, YYYY')}</small>
                                        <div className="d-flex flex-wrap">
                                          {upcomingSaturdayData.residents.map((resident, idx) => (
                                            <span key={idx} className={`small ${capitalizeName(resident.name) === currentResidentName ? 'text-danger' : 'text-muted'} me-1 mb-1`}>
                                              {capitalizeName(resident.name)}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {isOnCallUpcomingSunday && upcomingSundayData && (
                                      <div>
                                        <small className="mb-1 fw-semibold">Sunday: {moment(upcomingSundayData.date).format('MMM D, YYYY')}</small>
                                        <div className="d-flex flex-wrap">
                                          {upcomingSundayData.residents.map((resident, idx) => (
                                            <span key={idx} className={`small ${capitalizeName(resident.name) === currentResidentName ? 'text-danger' : 'text-muted'} me-1 mb-1`}>
                                              {capitalizeName(resident.name)}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted p-2 bg-light rounded-3 mb-2">
                                  <img src="/images/undraw_relaxing-at-home_qta9.svg" alt="No weekend call" className="img-fluid mb-2" style={{ maxHeight: '100px' }} />
                                  <p className="mb-0 small">You are not on call next week</p>
                                </div>
                              )
                            ) : (
                              <div className="text-muted">
                                <em>No weekend call scheduled</em>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </Col>
                  <Col lg={4} md={12} sm={12} className="mt-2 mt-lg-0 mt-md-2 mt-sm-0">
                    {/* Upcoming Vacations Section */}
                    {upcomingVacations && upcomingVacations.upcomingVacations && upcomingVacations.upcomingVacations.length > 0 ? (
                      <div>
                        <small className="text-muted mb-3">
                          Upcoming Leave
                        </small>
                        {upcomingVacations.upcomingVacations.slice(0, 2).map((vacation, index) => (
                          <div key={index} className=" mb-2">
                            <div className="bg-light rounded-3 p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <span className={`badge ${vacation.type === 'Vacation' ? 'bg-primary' : 'bg-warning'}`}>
                                  {vacation.type}
                                </span>                            
                                <small className={`${vacation.type === 'Vacation' ? 'text-primary' : 'text-warning'}`}>
                                  {moment(vacation.startDate).diff(moment(), 'days')} days away
                                </small>
                              </div>
                              <div className="d-flex justify-content-between align-items-center">
                                <small className="fw-semibold">
                                  {moment(vacation.startDate).format('MMM D')} - {moment(vacation.endDate).format('MMM D')}
                                </small> 
                                <small className="text-muted">
                                  {moment(vacation.endDate).diff(moment(vacation.startDate), 'days') + 1} days
                                </small>
                              </div>
                            </div>
                          </div>
                        ))}
                        {upcomingVacations.totalUpcoming > 2 && (
                          <div className="text-center mt-2">
                            <small className="text-muted">
                              +{upcomingVacations.totalUpcoming - 2} more upcoming
                            </small>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="d-flex flex-column h-100">
                        <small className="text-muted mb-2">Upcoming Leave</small>
                        <div className="border-alt bg-white rounded-3 p-3 d-flex flex-column align-items-center justify-content-center h-100">
                        <img src="/images/undraw_amusement-park_j8fe.svg" alt="No upcoming leave" className="img-fluid" style={{ maxHeight: '100px' }} />
                          <div className="d-flex align-items-center mt-2 text-muted small">
                            <span>No upcoming leave</span>
                          </div>

                        </div>
                      </div>
                    )}
                  </Col>
                </Row>
              ) : (
                <div className="text-center py-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 text-muted">Loading rotation information...</p>
                </div>
              )}

              {/* Upcoming Conferences - Always show, independent of rotationInfo */}
              {allConferences !== null && (() => {
                const startOfWeek = moment().startOf("week");
                const endOfWeek = moment().endOf("week");
                const startOfNextWeek = moment().add(1, "week").startOf("week");
                const endOfNextWeek = moment().add(1, "week").endOf("week");
                
                console.log("Week boundaries:", {
                  today: moment().format("YYYY-MM-DD"),
                  thisWeek: { start: startOfWeek.format("YYYY-MM-DD (ddd)"), end: endOfWeek.format("YYYY-MM-DD (ddd)") },
                  nextWeek: { start: startOfNextWeek.format("YYYY-MM-DD (ddd)"), end: endOfNextWeek.format("YYYY-MM-DD (ddd)") },
                  view: conferenceView,
                  allConferencesCount: allConferences.length,
                  sampleDates: allConferences.slice(0, 5).map(c => c.date)
                });
                
                // Filter conferences based on selected view
                const displayedConferences = allConferences.filter((conf) => {
                  const dateMoment = moment(conf.date);
                  let isInRange = false;
                  
                  if (conferenceView === 'thisWeek') {
                    isInRange = dateMoment.isSameOrAfter(startOfWeek, "day") && dateMoment.isSameOrBefore(endOfWeek, "day");
                  } else {
                    isInRange = dateMoment.isSameOrAfter(startOfNextWeek, "day") && dateMoment.isSameOrBefore(endOfNextWeek, "day");
                  }
                  
                  if (!isInRange && conferenceView === 'thisWeek') {
                    console.log(`Conference ${conf.title} on ${conf.date}:`, {
                      dateMoment: dateMoment.format("YYYY-MM-DD (ddd)"),
                      isInRange,
                      view: conferenceView,
                      startOfWeek: startOfWeek.format("YYYY-MM-DD"),
                      endOfWeek: endOfWeek.format("YYYY-MM-DD"),
                      isBeforeStart: dateMoment.isBefore(startOfWeek, "day"),
                      isAfterEnd: dateMoment.isAfter(endOfWeek, "day")
                    });
                  }
                  
                  return isInRange;
                });
                
                console.log("Displayed conferences:", displayedConferences);

                return (
                  <Row className="mt-3">
                    <Col lg={12}>
                      <div className="d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="text-muted fw-normal">
                            Conferences
                          </small>
                          <div className="radio-inputs" style={{ maxWidth: '180px' }}>
                            <label className="radio">
                              <input
                                type="radio"
                                name="conference-view-radio"
                                checked={conferenceView === 'thisWeek'}
                                onChange={() => setConferenceView('thisWeek')}
                                aria-label="This Week"
                              />
                              <span className="name">This Week</span>
                            </label>
                            <label className="radio">
                              <input
                                type="radio"
                                name="conference-view-radio"
                                checked={conferenceView === 'upcoming'}
                                onChange={() => setConferenceView('upcoming')}
                                aria-label="Upcoming"
                              />
                              <span className="name">Upcoming</span>
                            </label>
                          </div>
                        </div>
                        {displayedConferences && displayedConferences.length > 0 ? (
                          <div className="d-flex flex-column gap-2">
                            {displayedConferences.map((conf) => (
                            <div
                              key={conf.id}
                              className="bg-light custom-hover-bg rounded-3 p-3 d-flex justify-content-between align-items-start"
                              onClick={() => navigate("/resident/conferences")}

                            >
                              <div className="d-flex flex-column">
                                <span className="fw-semibold small">
                                  {conf.title || "Untitled Conference"}
                                </span>
                                <small className="text-muted d-flex align-items-center flex-wrap gap-2">
                                  <span>
                                    {moment(conf.date).format("MMM D, YYYY")}
                                    {conf.startTime
                                      ? ` • ${moment(conf.startTime, "HH:mm").format("h:mm A")}`
                                      : ""}
                                  </span>
                                  {conf.location && (
                                    <span>
                                      <i className="bi bi-geo-alt me-1" />
                                      {conf.location}
                                    </span>
                                  )}
                                </small>
                              </div>
                              {conf.type && (() => {
                                const typeInfo = renderConferenceType(conf.type);
                                return (
                                  <Badge bg={typeInfo.color}>
                                    {typeInfo.label}
                                  </Badge>
                                );
                              })()}
                            </div>
                            ))}
                          </div>
                        ) : (
                          <div className="border rounded p-3 bg-light">
                            <div className="d-flex align-items-center gap-2 text-muted small">
                              <i className="bi bi-calendar-event"></i>
                              <span>No conferences {conferenceView === 'thisWeek' ? 'this week' : 'next week'}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                );
              })()}
            </Card.Body>
          </Card>

          {/* Case Statistics Card */}
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body className="p-md-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex flex-column align-items-start justify-content-start">
                  <h6 className="mb-0 fw-semibold">Case Log Statistics</h6>
                  <p className="mb-0 text-muted small">Snapshot of your case log statistics to date</p>
                </div>
                <div className="d-flex align-items-center justify-content-center gap-2">
                <Button 
                  variant="light-solid" 
                  size="sm"
                  onClick={() => {
                    if (!showDetailedStats) {
                      // Any data fetching if needed
                    }
                    setShowDetailedStats(!showDetailedStats);
                  }}
                >
                  {showDetailedStats ? (
                    <>
                      <span className="d-none d-md-block">Show Less</span><i className="bi bi-bar-chart-fill d-block d-md-none"></i>
                    </>
                  ) : (
                    <>
                      <span className="d-none d-md-block">View Stats</span><i className="bi bi-bar-chart d-block d-md-none"></i>
                    </>
                  )}
                </Button>
                <Button 
                  variant="light-solid" 
                  size="sm" 
                  onClick={() => navigate("/resident/case-log-page")}
                >
                  <i className="bi bi-arrow-right"></i>
                </Button></div>
              </div>
              
              {caseStats ? (
                <>
                  {/* Summary Statistics - Simple Design */}
                  <div className="d-flex justify-content-between align-items-start gap-2 gap-md-5 flex-wrap ">
                    <div className="flex-fill p-2">
                      <div className="text-muted small mb-1">Total Cases</div>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center justify-content-start gap-1">
                        <span className="fw-bold fs-3" >{caseStats.totalCases || 0} </span>
                          {(persistedCaseTrends.totalCases || caseStats.trends?.totalCases) === 'up' && <span className="rounded px-1 py-0 bg-success"><i className="bi bi-arrow-up text-success" style={{ fontSize: '16px' }}></i></span>}
                          {(persistedCaseTrends.totalCases || caseStats.trends?.totalCases) === 'down' && <span className="rounded px-1 py-0 bg-danger"><i className="bi bi-arrow-down text-danger" style={{ fontSize: '16px' }}></i></span>}
                          {(persistedCaseTrends.totalCases || caseStats.trends?.totalCases) === 'neutral' && <span className="rounded px-1 py-0 bg-secondary"><i className="bi bi-dash text-muted" style={{ fontSize: '16px' }}></i></span>}
                        </div>
                          <i className="bi bi-file-medical text-muted opacity-50"></i>
                        
                      </div>
                    </div>
                    <div className="flex-fill p-2">
                      <div className="text-muted small mb-1">Last 30 Days</div>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center justify-content-start gap-1">
                        <span className="fw-bold fs-3">{caseStats.recentCases || 0} </span>
                        {(persistedCaseTrends.recentCases || caseStats.trends?.recentCases) === 'up' && <span className="rounded px-1 py-0 bg-success"><i className="bi bi-arrow-up text-success" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.recentCases || caseStats.trends?.recentCases) === 'down' && <span className="rounded px-1 py-0 bg-danger"><i className="bi bi-arrow-down text-danger" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.recentCases || caseStats.trends?.recentCases) === 'neutral' && <span className="rounded px-1 py-0 bg-secondary"><i className="bi bi-dash text-muted" style={{ fontSize: '16px' }}></i></span>}
                        </div>
                        <i className="bi bi-calendar-check text-muted opacity-50"></i>
                      </div>
                    </div>
                    <div className="flex-fill p-2">
                      <div className="text-muted small mb-1">Robotic Cases</div>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center justify-content-start gap-1">
                        <span className="fw-bold fs-3">{caseStats.roboticCases || 0} </span>
                        {(persistedCaseTrends.roboticCases || caseStats.trends?.roboticCases) === 'up' && <span className="rounded px-1 py-0 bg-success"><i className="bi bi-arrow-up text-success" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.roboticCases || caseStats.trends?.roboticCases) === 'down' && <span className="rounded px-1 py-0 bg-danger"><i className="bi bi-arrow-down text-danger" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.roboticCases || caseStats.trends?.roboticCases) === 'neutral' && <span className="rounded px-1 py-0 bg-secondary"><i className="bi bi-dash text-muted" style={{ fontSize: '16px' }}></i></span>}
                        </div>
                        <i className="bi bi-robot text-muted opacity-50"></i>
                      </div>
                    </div>
                    <div className="flex-fill p-2">
                      <div className="text-muted small mb-1">Min. Invasive</div>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center justify-content-start gap-1">
                        <span className="fw-bold fs-3">{caseStats.minimallyInvasiveCases || 0} </span>
                        {(persistedCaseTrends.minimallyInvasiveCases || caseStats.trends?.minimallyInvasiveCases) === 'up' && <span className="rounded px-1 py-0 bg-success"><i className="bi bi-arrow-up text-success" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.minimallyInvasiveCases || caseStats.trends?.minimallyInvasiveCases) === 'down' && <span className="rounded px-1 py-0 bg-danger"><i className="bi bi-arrow-down text-danger" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.minimallyInvasiveCases || caseStats.trends?.minimallyInvasiveCases) === 'neutral' && <span className="rounded px-1 py-0 bg-secondary"><i className="bi bi-dash text-muted" style={{ fontSize: '16px' }}></i></span>}
                        </div>
                        <i className="bi bi-scissors text-muted opacity-50"></i>
                      </div>
                    </div>
                    <div className="flex-fill p-2">
                      <div className="text-muted small mb-1">Open Cases</div>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center justify-content-start gap-1">
                        <span className="fw-bold fs-3">{caseStats.openCases || 0} </span>
                        {(persistedCaseTrends.openCases || caseStats.trends?.openCases) === 'up' && <span className="rounded px-1 py-0 bg-success"><i className="bi bi-arrow-up text-success" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.openCases || caseStats.trends?.openCases) === 'down' && <span className="rounded px-1 py-0 bg-danger"><i className="bi bi-arrow-down text-danger" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.openCases || caseStats.trends?.openCases) === 'neutral' && <span className="rounded px-1 py-0 bg-secondary"><i className="bi bi-dash text-muted" style={{ fontSize: '16px' }}></i></span>}
                        </div>
                        <i className="bi bi-person-lines-fill text-muted opacity-50"></i>
                      </div>
                    </div>
                    <div className="flex-fill p-2">
                      <div className="text-muted small mb-1">Academic Year</div>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center justify-content-start gap-1">
                        <span className="fw-bold fs-3">{caseStats.thisAcademicYearCases || 0} </span>
                        {(persistedCaseTrends.thisAcademicYearCases || caseStats.trends?.thisAcademicYearCases) === 'up' && <span className="rounded px-1 py-0 bg-success"><i className="bi bi-arrow-up text-success" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.thisAcademicYearCases || caseStats.trends?.thisAcademicYearCases) === 'down' && <span className="rounded px-1 py-0 bg-danger"><i className="bi bi-arrow-down text-danger" style={{ fontSize: '16px' }}></i></span>}
                        {(persistedCaseTrends.thisAcademicYearCases || caseStats.trends?.thisAcademicYearCases) === 'neutral' && <span className="rounded px-1 py-0 bg-secondary"><i className="bi bi-dash text-muted" style={{ fontSize: '16px' }}></i></span>}
                        </div>
                        <i className="bi bi-calendar-event text-muted opacity-50"></i>
                      </div>
                    </div>
                  </div>

                  {/* Detailed Statistics - Collapsible */}
                  {showDetailedStats && (
                    <div className="border-top pt-4 mt-4">
                      <CaseStatisticsPage embedded residentId="me" />
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 text-muted">Loading case statistics...</p>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Assigned Evaluations Card */}
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body className="p-md-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                
                <div className="d-flex flex-column align-items-start justify-content-start">
                <h6 className="mb-0 fw-semibold">
                  Assigned Evaluations
                  {assignedEvals.length > 0 && (
                    <span className="badge bg-danger ms-2 px-3 py-1">{assignedEvals.length}</span>
                  )}
                  
                </h6>
                <p className="mb-0 text-muted small">Your Pending  Assigned evaluations</p>
                </div>
                <div className="d-flex align-items-center justify-content-center gap-2">
                   {/* Show More/Less Button */}
                    {assignedEvals.length > 5 && (
                    <div className="text-center">
                      <Button 
                        variant="light-solid" 
                        size="sm"
                        onClick={() => setShowAllEvaluations(!showAllEvaluations)}
                        style={{borderRadius: '20px !important'}}
                      >
                        <i className={` ${showAllEvaluations ? 'bi-card-checklist' : 'bi-list'} d-block d-md-none`}></i>
                        <span className="d-none d-md-inline ms-1">{showAllEvaluations ? 'Show Less' : `Show ${assignedEvals.length - 5} More`}</span>
                      </Button>
                    </div>
                  )}
                {/*<Button 
                  variant="outline-primary" 
                  size="sm" 
                  onClick={() => navigate("/resident/evaluation")}
                >
                  <i className="bi bi-arrow-right"></i>
                </Button>*/}
                </div>
              </div>
              
              {assignedEvals.length > 0 ? (
                <div className="d-flex flex-column gap-2">
                  {(showAllEvaluations ? assignedEvals : assignedEvals.slice(0, 5)).map((evaluation) => (
                    <div className="d-flex flex-row align-items-center justify-content-between p-md-3 p-2 rounded border-alt-light mx-0 custom-hover-bg-alt"
                      key={evaluation.id}   
                      onClick={() => navigate(`/resident/evaluation/fill/${evaluation.id}`)}
                    >
                      <div className="d-flex flex-row align-items-center justify-content-start gap-3">
                      <div className="col-md-auto d-flex align-items-start justify-content-start">
                        <div className="d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, backgroundColor: getEvaluationIconBg(evaluation.template?.type), borderRadius: 6 }}>
                          {getEvaluationIcon(evaluation.template?.type)}
                        </div>
                      </div>
                      <div className="d-flex flex-column">
                        <div className="fw-semibold small">
                          {evaluation.title}
                        </div>
                        <div className="text-muted small">
                          Due: {new Date(evaluation.endTime).toLocaleDateString()}
                          {evaluation.template?.type && (
                            <span className="ms-2">• {evaluation.template.type}</span>
                          )}
                        </div>
                      </div>
                      </div>
                      <div className="d-flex align-items-end justify-content-end">
                        {new Date() > new Date(evaluation.endTime) ? (
                          <Badge bg="danger" className="rounded-2">
                            <i className="bi bi-clock"></i>
                           <span className="d-none d-md-inline ms-1">Overdue</span>
                          </Badge>
                        ) : (
                          <Badge bg="success" className="rounded-2">
                            Active
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                  
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-clipboard-check" style={{ fontSize: 48, opacity: 0.3 }}></i>
                  <p className="mt-2">No evaluations assigned</p>
                </div>
              )}
            </Card.Body>
          </Card>
          </Col>
          <Col lg={3} md={5} sm={12}>
            <DashboardCalendarWidget />
            <Card className="shadow-sm border-0 mt-4">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex flex-column justify-content-start">
                    <h6 className="fw-semibold mb-0">Important Resources</h6>
                  </div>
                  <Button 
                  variant="light-solid" 
                  size="sm" 
                  onClick={() => navigate("/resources")}
                >
                  <i className="bi bi-arrow-right"></i>
                </Button>
                </div>

                <div className="mt-3">
                  {sidebarSectionsLoading ? (
                    <div className="small text-muted mb-3">Loading links...</div>
                  ) : resourceLinkSections.length === 0 ? (
                    <div className="small text-muted mb-3">No links available</div>
                  ) : (
                    resourceLinkSections.map((section) => {
                      const sectionItems = Array.isArray(section.items) ? section.items : [];
                      return (
                        <div key={section.id} className="mb-4">
                          <h6 className="fw-semibold mb-0 small">
                            {section.name || "Resource Links"}
                          </h6>
                          {!!section.direction && (
                            <p className="mb-2 text-muted small">{section.direction}</p>
                          )}
                          {sectionItems.length === 0 ? (
                            <div className="small text-muted">No links available</div>
                          ) : (
                            <div className="d-flex flex-column gap-1">
                              {sectionItems.slice(0, 6).map((item) => (
                                <div key={item.id} className="rounded px-2 py-1 custom-hover-bg-alt">
                                  <a
                                    href={item.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="d-flex align-items-center justify-content-between fw-semibold text-decoration-none small"
                                  >
                                    <span className="text-truncate me-2">{item.label || item.url}</span>
                                    <i className="bi bi-link-45deg flex-shrink-0" />
                                  </a>
                                  {!!item.description && (
                                    <small className="text-muted d-block mt-1 text-truncate">{item.description}</small>
                                  )}
                                </div>
                              ))}
                              {sectionItems.length > 6 && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-start"
                                  onClick={() => navigate("/resources")}
                                >
                                  View all links
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}

                  <div>
                  {sidebarSectionsLoading ? (
                    <div className="small text-muted">Loading documents...</div>
                  ) : resourceDocumentSections.length === 0 ? (
                    <div className="small text-muted">No documents available</div>
                  ) : (
                    resourceDocumentSections.map((section) => {
                      const sectionItems = Array.isArray(section.items) ? section.items : [];
                      return (
                        <div key={section.id} className="mb-4">
                          <h6 className="fw-semibold mb-0 small">
                            {section.name || "Resource Documents"}
                          </h6>
                          {!!section.direction && (
                            <p className="mb-2 text-muted small">{section.direction}</p>
                          )}
                          {sectionItems.length === 0 ? (
                            <div className="small text-muted">No documents available</div>
                          ) : (
                            <div className="d-flex flex-column gap-2">
                              {sectionItems.slice(0, 6).map((item) => (
                                <div key={item.id} className=" custom-hover-bg-alt rounded px-2 py-1">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-1 fw-semibold overflow-hidden">
                                      <i className={`bi ${getFileIcon(item.fileName, item.mimeType)} flex-shrink-0`} />
                                      <small className="text-truncate">
                                        {item.alternateName || item.originalName || item.fileName}
                                      </small>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="light-lite"
                                      className="p-0 px-1 flex-shrink-0"
                                      title="Download"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        downloadFile({
                                          webPath: item.webPath,
                                          originalName: item.originalName || item.fileName,
                                          fileName: item.fileName,
                                        });
                                      }}
                                    >
                                      <i className="bi bi-download" />
                                    </Button>
                                  </div>
                                  {!!item.description && (
                                    <small className="text-muted d-block mt-1 text-truncate">{item.description}</small>
                                  )}
                                </div>
                              ))}
                              {sectionItems.length > 6 && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-start"
                                  onClick={() => navigate("/resources")}
                                >
                                  View all documents
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ResidentDashboard;
